import { useEffect, useRef, useState } from "react";

const THRESHOLD = 76;
const MAX_PULL = 120;

function pullBlocked(target) {
  if (window.scrollY > 1) return true;
  if (target instanceof Element && target.closest("[role='dialog'], .p-dialog, .p-dialog-mask")) {
    return true;
  }

  let node = target instanceof Element ? target : null;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const scrollable = /(auto|scroll|overlay)/.test(style.overflowY);
    if (scrollable && node.scrollHeight > node.clientHeight + 2 && node.scrollTop > 0) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

function PullToRefresh() {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const startX = useRef(0);
  const tracking = useRef(false);
  const distanceRef = useRef(0);

  useEffect(() => {
    const updateDistance = (value) => {
      distanceRef.current = value;
      setDistance(value);
    };

    const onStart = (event) => {
      if (refreshing || event.touches.length !== 1) return;
      if (pullBlocked(event.target)) return;
      startY.current = event.touches[0].clientY;
      startX.current = event.touches[0].clientX;
      tracking.current = true;
    };

    const onMove = (event) => {
      if (!tracking.current || event.touches.length !== 1) return;
      const dy = event.touches[0].clientY - startY.current;
      const dx = event.touches[0].clientX - startX.current;
      if (dy <= 0) {
        updateDistance(0);
        return;
      }
      if (Math.abs(dx) > Math.abs(dy) || window.scrollY > 1) {
        tracking.current = false;
        updateDistance(0);
        return;
      }
      event.preventDefault();
      updateDistance(Math.min(dy * 0.42, MAX_PULL));
    };

    const onEnd = () => {
      if (!tracking.current) return;
      tracking.current = false;
      if (distanceRef.current >= THRESHOLD) {
        setRefreshing(true);
        updateDistance(THRESHOLD);
        window.setTimeout(() => window.location.reload(), 220);
        return;
      }
      updateDistance(0);
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing]);

  const visible = distance > 6 || refreshing;
  const ready = refreshing || distance >= THRESHOLD;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center"
      style={{ top: "calc(env(safe-area-inset-top) + 4.1rem)" }}
    >
      <div
        className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-primary shadow-lg"
        style={{
          opacity: visible ? 1 : 0,
          transform: `translateY(${visible ? Math.max(distance - 28, 0) : -16}px) scale(${visible ? 1 : 0.8})`,
          transition: distance > 0 && !refreshing ? "none" : "opacity 0.2s ease, transform 0.2s ease",
        }}
        role="status"
        aria-live="polite"
        aria-label={ready ? "Melepas akan memuat ulang" : "Tarik untuk memuat ulang"}
      >
        <svg
          className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`}
          style={refreshing ? undefined : { transform: `rotate(${Math.min(distance / THRESHOLD, 1) * 180}deg)` }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0112.8-5.3M19.5 12a7.5 7.5 0 01-12.8 5.3" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.2 3.8h3.1V7M7.8 20.2H4.7V17" />
        </svg>
      </div>
    </div>
  );
}

export default PullToRefresh;
